import CommonService from "../Common";
import { AxiosRequestConfig } from "axios";
import config from "../../config";
import {
  mockUsageRecords,
  mockUsageErrors,
} from "../../mock/dashboardMockData";

export interface UsageItemData {
  unit_type: string;
  unit_name?: string;
  quantity: number;
  rate_per_unit: number;
  item_cost: number;
  model_name?: string;
  feature_name?: string;
  attributes?: Record<string, any>;
}

export interface UsageData {
  _id?: string;
  user_id?: string;
  platform_id: string;
  platform_name?: string;
  date: Date | string;
  total_cost?: number;
  usage_items: UsageItemData[];
  createdAt?: Date | string;
  updatedAt?: Date | string;
}

interface UsageListQueryParams {
  page?: number;
  limit?: number;
  sortBy?: string;
  sortOrder?: "asc" | "desc";
  platformId?: string;
  startDate?: string;
  endDate?: string;
}

class UsageService {
  pathPrefix: string;
  commonService: CommonService;

  constructor() {
    this.pathPrefix = "/api/v1/usage";
    this.commonService = new CommonService();
  }

  // Get all usage records with pagination and filters
  async getAllUsage(params: UsageListQueryParams = {}, userId: string) {
    // Return mock data when not in production
    if (!config.inProd) {
      let filteredRecords = [...mockUsageRecords];

      // Apply platform filter if specified
      if (params.platformId) {
        filteredRecords = filteredRecords.filter(
          (record) => record.platform_id === params.platformId
        );
      }

      // Apply date range filters if specified
      if (params.startDate) {
        const startDate = new Date(params.startDate);
        filteredRecords = filteredRecords.filter(
          (record) => new Date(record.date) >= startDate
        );
      }

      if (params.endDate) {
        const endDate = new Date(params.endDate);
        filteredRecords = filteredRecords.filter(
          (record) => new Date(record.date) <= endDate
        );
      }

      // Sort records
      const sortField = params.sortBy || "date";
      const sortOrder = params.sortOrder || "desc";

      filteredRecords.sort((a, b) => {
        if (sortField === "date") {
          return sortOrder === "asc"
            ? new Date(a.date).getTime() - new Date(b.date).getTime()
            : new Date(b.date).getTime() - new Date(a.date).getTime();
        } else if (sortField === "total_cost") {
          return sortOrder === "asc"
            ? (a.total_cost || 0) - (b.total_cost || 0)
            : (b.total_cost || 0) - (a.total_cost || 0);
        }
        // Default sort by date
        return sortOrder === "asc"
          ? new Date(a.date).getTime() - new Date(b.date).getTime()
          : new Date(b.date).getTime() - new Date(a.date).getTime();
      });

      // Apply pagination
      const page = params.page || 1;
      const limit = params.limit || 10;
      const startIndex = (page - 1) * limit;
      const endIndex = page * limit;
      const paginatedRecords = filteredRecords.slice(startIndex, endIndex);

      // Adjust pagination info based on filtered results
      const totalRecords = filteredRecords.length;
      const totalPages = Math.ceil(totalRecords / limit);

      const paginationInfo = {
        total: totalRecords,
        page,
        limit,
        pages: totalPages,
      };

      return Promise.resolve({
        data: paginatedRecords,
        pagination: paginationInfo,
        success: true,
      });
    }

    const requestConfig: AxiosRequestConfig = {
      method: "POST",
      url: `${this.pathPrefix}`,
      params: { ...params },
      data: { userId },
      withCredentials: true,
    };
    return this.commonService.fetch(requestConfig);
  }

  // Get a specific usage record by ID
  async getUsage(id: string) {
    // Return mock data when not in production
    if (!config.inProd) {
      const record = mockUsageRecords.find((item) => item._id === id);

      if (record) {
        return Promise.resolve({ usage: record, success: true });
      } else {
        return Promise.reject({ ...mockUsageErrors.notFound, status: 404 });
      }
    }

    const requestConfig: AxiosRequestConfig = {
      method: "GET",
      url: `${this.pathPrefix}/${id}`,
      withCredentials: true,
    };
    return this.commonService.fetch(requestConfig);
  }

  // Create a new usage record
  async createUsage(usageData: UsageData, userId: string) {
    // Mock response when not in production
    if (!config.inProd) {
      // Validate required fields
      if (
        !usageData.platform_id ||
        !usageData.date ||
        !usageData.usage_items ||
        usageData.usage_items.length === 0
      ) {
        return Promise.reject({
          ...mockUsageErrors.validationFailure,
          status: 400,
        });
      }

      // Check for duplicate date/platform combination
      const isDuplicate = mockUsageRecords.some(
        (record) =>
          record.platform_id === usageData.platform_id &&
          new Date(record.date).toDateString() ===
            new Date(usageData.date).toDateString()
      );

      if (isDuplicate) {
        return Promise.reject({
          ...mockUsageErrors.duplicateEntry,
          status: 400,
        });
      }

      // Calculate total cost if not provided
      if (!usageData.total_cost) {
        usageData.total_cost = this.calculateTotalCost(usageData.usage_items);
      }

      // Create a new record with mock ID and timestamps
      const newRecord: UsageData = {
        _id: `mock-id-${Date.now()}`,
        user_id: userId,
        ...usageData,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };

      return Promise.resolve({
        message: "Usage record created successfully",
        usage: newRecord,
        success: true,
      });
    }

    const requestConfig: AxiosRequestConfig = {
      method: "POST",
      url: `${this.pathPrefix}`,
      data: { ...usageData, userId },
      withCredentials: true,
    };
    return this.commonService.fetch(requestConfig);
  }

  // Create multiple usage records in batch
  async batchCreateUsage(usageDataArray: UsageData[], userId: string) {
    // Mock response when not in production
    if (!config.inProd) {
      if (
        !usageDataArray ||
        !Array.isArray(usageDataArray) ||
        usageDataArray.length === 0
      ) {
        return Promise.reject({
          error: "Invalid input",
          details: "Usage data array is required",
          status: 400,
        });
      }

      // Process each record
      const results = usageDataArray.map((usageData) => {
        // Validate required fields
        if (
          !usageData.platform_id ||
          !usageData.date ||
          !usageData.usage_items ||
          usageData.usage_items.length === 0
        ) {
          return {
            success: false,
            error: "Validation failed",
            details: "Missing required fields",
          };
        }

        // Check for duplicate date/platform combination
        const isDuplicate = mockUsageRecords.some(
          (record) =>
            record.platform_id === usageData.platform_id &&
            new Date(record.date).toDateString() ===
              new Date(usageData.date).toDateString()
        );

        if (isDuplicate) {
          return {
            success: false,
            error: "Duplicate entry",
            details: "A usage record for this platform and date already exists",
          };
        }

        // Calculate total cost if not provided
        if (!usageData.total_cost) {
          usageData.total_cost = this.calculateTotalCost(usageData.usage_items);
        }

        // Create a new record with mock ID and timestamps
        return {
          success: true,
          usage: {
            _id: `mock-id-${Date.now()}-${Math.random()
              .toString(36)
              .substring(2, 9)}`,
            user_id: userId,
            ...usageData,
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
          },
        };
      });

      const successCount = results.filter((result) => result.success).length;

      return Promise.resolve({
        message: `${successCount} of ${usageDataArray.length} usage records created successfully`,
        results,
        success: true,
      });
    }

    const requestConfig: AxiosRequestConfig = {
      method: "POST",
      url: `${this.pathPrefix}/batch`,
      data: { usage: usageDataArray, userId },
      withCredentials: true,
    };
    return this.commonService.fetch(requestConfig);
  }

  // Update an existing usage record
  async updateUsage(id: string, usageData: Partial<UsageData>, userId: string) {
    // Mock response when not in production
    if (!config.inProd) {
      const recordIndex = mockUsageRecords.findIndex((item) => item._id === id);

      if (recordIndex === -1) {
        return Promise.reject({ ...mockUsageErrors.notFound, status: 404 });
      }

      // Recalculate total cost if usage items are updated
      if (usageData.usage_items) {
        usageData.total_cost = this.calculateTotalCost(usageData.usage_items);
      }

      // Create updated record
      const updatedRecord = {
        ...mockUsageRecords[recordIndex],
        ...usageData,
        updatedAt: new Date().toISOString(),
      };

      return Promise.resolve({
        message: "Usage record updated successfully",
        usage: updatedRecord,
        success: true,
      });
    }

    const requestConfig: AxiosRequestConfig = {
      method: "PUT",
      url: `${this.pathPrefix}/${id}`,
      data: { ...usageData, userId },
      withCredentials: true,
    };
    return this.commonService.fetch(requestConfig);
  }

  // Delete a usage record
  async deleteUsage(id: string, userId: string) {
    // Mock response when not in production
    if (!config.inProd) {
      const recordIndex = mockUsageRecords.findIndex((item) => item._id === id);

      if (recordIndex === -1) {
        return Promise.reject({ ...mockUsageErrors.notFound, status: 404 });
      }

      return Promise.resolve({
        message: "Usage record deleted successfully",
        usage: mockUsageRecords[recordIndex],
        success: true,
      });
    }

    const requestConfig: AxiosRequestConfig = {
      method: "DELETE",
      url: `${this.pathPrefix}/${id}`,
      data: { userId },
      withCredentials: true,
    };
    return this.commonService.fetch(requestConfig);
  }

  // Helper to format date for API requests
  formatDateForAPI(date: Date): string {
    return date.toISOString();
  }

  // Helper to calculate total cost from usage items
  calculateTotalCost(usageItems: UsageItemData[]): number {
    return usageItems.reduce((sum, item) => sum + item.item_cost, 0);
  }
}

export default UsageService;
